import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import HelpPage from './components/HelpPage';
import SiteNav from './components/SiteNav';
import { NAV_LINKS } from './navLinks';
import axios from 'axios';
import { X, ArrowRight, CheckCircle, Send, Linkedin, Twitter, Instagram, Youtube, Mail } from 'lucide-react';
import './App.css';
import { pageview, trackDemoRequest, trackPartnershipApplication, trackContactSubmit, trackCTAClick, trackFormStart, trackFormError, trackSectionView, trackOutboundClick, trackFAQExpand } from './analytics';
import PricingSection from './components/PricingSection';
import PlanInterestModal from './components/PlanInterestModal';
import LegalDocumentModal from './components/LegalDocumentModal';
import CookieConsentBanner from './components/CookieConsentBanner';
import CountryCodePickerComponent, { COUNTRY_CODES } from './components/CountryCodePicker';
import { TurnstileWidget } from './components/TurnstileWidget';
import { getConsent, setConsent, loadGA, initAnalyticsFromConsent } from './consent';
import studentsIllustration from './assets/students-illustration.png';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

/* ─── Mock product UI data ─────────────────────────────────────── */

const APPROVAL_SEQUENCE = [
  {
    label: 'Campaign Received',
    desc: 'A student has submitted a new funding campaign for review.',
    reviewer: null,
    duration: 2800,
  },
  {
    label: 'Institution Verified',
    desc: 'Enrollment and institution credentials confirmed.',
    reviewer: 'Verified by the Registrar\'s Office',
    duration: 3200,
  },
  {
    label: 'Academic Review',
    desc: 'Academic standing and eligibility validated by faculty.',
    reviewer: 'Reviewed by Academic Affairs Committee',
    duration: 3200,
  },
  {
    label: 'Financial Assessment',
    desc: 'Funding amount and disbursement plan approved.',
    reviewer: 'Approved by Finance Dept.',
    duration: 3600,
  },
  {
    label: 'Campaign Published',
    desc: 'Live on the platform — supporters can now contribute to this campaign.',
    reviewer: null,
    duration: 2800,
  },
];

// COUNTRY_CODES imported from ./components/CountryCodePicker

/* ─── Mock data for new previews ─────────────────────────────── */
const SUPPORTER_CAMPAIGNS = [
  { name: 'Ayesha Khan',   batch: '2022–2026', course: 'B.Sc Computer Science',        title: 'Help Ayesha complete her Computer Science degree',  raised: 68000,  goal: 90000,  supporters: 47, avatar: 'AK', pct: 75 },
  { name: 'Rahul Mehta',   batch: '2021–2025', course: 'B.E. Mechanical Engineering',  title: "Support Rahul's final year engineering project",     raised: 44000,  goal: 70000,  supporters: 38, avatar: 'RM', pct: 63 },
  { name: 'Priya Sharma',  batch: '2023–2027', course: 'B.Com Honours',                title: 'Tuition assistance for Priya Sharma',                raised: 31500,  goal: 55000,  supporters: 24, avatar: 'PS', pct: 57 },
];

const STUDENT_CAMPAIGNS_DATA = [
  { title: 'Final year tuition and examination fees', target: 85000, status: 'Active', color: '#2563EB', from: '01/02/2026', to: '30/04/2026', raised: 52000, pct: 61 },
  { title: 'Semester fee support — 6th semester', target: 45000, status: 'Completed', color: '#16a34a', from: '01/09/2025', to: '30/11/2025', raised: 45000, pct: 100 },
];

/* ─── Sub-components ───────────────────────────────────────────── */



function ApprovalTimeline() {
  /* ── State machine ──
     activeStep: -1 = intro, 0–4 = animating that step, 5 = success hold
     Phase per step: 'entering' → 'processing' → 'done'
     The whole sequence loops automatically.                              */
  const [activeStep, setActiveStep] = useState(-1);
  const [stepPhase, setStepPhase] = useState('idle');   // 'entering' | 'processing' | 'done' | 'idle'
  const [completedSteps, setCompletedSteps] = useState([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const timerRef = useRef(null);

  const resetSequence = useCallback(() => {
    setActiveStep(-1);
    setStepPhase('idle');
    setCompletedSteps([]);
    setShowSuccess(false);
  }, []);

  useEffect(() => {
    const clear = () => { if (timerRef.current) clearTimeout(timerRef.current); };

    if (activeStep === -1) {
      // Intro pause — panel breathes before sequence starts
      timerRef.current = setTimeout(() => { setActiveStep(0); setStepPhase('entering'); }, 1200);
    } else if (activeStep >= 0 && activeStep < APPROVAL_SEQUENCE.length) {
      const step = APPROVAL_SEQUENCE[activeStep];

      if (stepPhase === 'entering') {
        // Row slides in, dot pops — reader has time to register it
        timerRef.current = setTimeout(() => setStepPhase('processing'), 900);
      } else if (stepPhase === 'processing') {
        // Soft sweep visible long enough to feel real
        const processingTime = activeStep === APPROVAL_SEQUENCE.length - 1 ? 1800 : step.duration;
        timerRef.current = setTimeout(() => setStepPhase('done'), processingTime);
      } else if (stepPhase === 'done') {
        // Check draws, line fills — brief pause before moving on
        setCompletedSteps(prev => [...prev, activeStep]);
        const isLast = activeStep === APPROVAL_SEQUENCE.length - 1;
        if (isLast) {
          timerRef.current = setTimeout(() => { setActiveStep(5); setShowSuccess(true); }, 900);
        } else {
          timerRef.current = setTimeout(() => {
            setActiveStep(prev => prev + 1);
            setStepPhase('entering');
          }, 800);
        }
      }
    } else if (activeStep === 5) {
      // Success hold — let them read "Campaign is live" comfortably
      timerRef.current = setTimeout(resetSequence, 5000);
    }

    return clear;
  }, [activeStep, stepPhase, resetSequence]);

  // Derive status tag
  const getTagState = () => {
    if (showSuccess) return { text: 'Published', className: 'atl-tag--success' };
    if (activeStep === -1) return { text: 'New Submission', className: 'atl-tag--new' };
    if (activeStep < APPROVAL_SEQUENCE.length) {
      return { text: `Step ${activeStep + 1} of ${APPROVAL_SEQUENCE.length}`, className: 'atl-tag--review' };
    }
    return { text: 'Published', className: 'atl-tag--success' };
  };

  const tag = getTagState();
  const progressPct = showSuccess ? 100 : Math.round((completedSteps.length / APPROVAL_SEQUENCE.length) * 100);

  return (
    <div className={`atl-panel${showSuccess ? ' atl-panel--success' : ''}`}>
      {/* Success shimmer overlay */}
      {showSuccess && <div className="atl-success-shimmer" />}

      {/* Header */}
      <div className="atl-header">
        <div className="atl-header-left">
          <div className="atl-icon">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 12l2 2 4-4"/><circle cx="12" cy="12" r="10"/></svg>
          </div>
          <div className="atl-header-text">
            <span className="atl-title">Campaign Approval</span>
            <span className="atl-subtitle">Institutional review workflow</span>
          </div>
        </div>
        <span className={`atl-tag ${tag.className}`} key={tag.text}>
          {tag.className === 'atl-tag--review' && <span className="atl-tag-dot" />}
          {tag.className === 'atl-tag--success' && (
            <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2.5 6.5L5 9L9.5 3.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
          )}
          {tag.text}
        </span>
      </div>

      {/* Timeline */}
      <div className="atl-timeline">
        {APPROVAL_SEQUENCE.map((step, i) => {
          const isCompleted = completedSteps.includes(i);
          const isActive = activeStep === i;
          const isProcessing = isActive && stepPhase === 'processing';
          const isEntering = isActive && stepPhase === 'entering';
          const isFuture = !isCompleted && !isActive;
          const isLastDone = i === APPROVAL_SEQUENCE.length - 1 && isCompleted;

          let rowClass = 'atl-step';
          if (isCompleted) rowClass += ' atl-step--done';
          if (isActive) rowClass += ' atl-step--active';
          if (isEntering) rowClass += ' atl-step--entering';
          if (isProcessing) rowClass += ' atl-step--processing';
          if (isFuture) rowClass += ' atl-step--future';
          if (isLastDone) rowClass += ' atl-step--published';

          return (
            <div key={i} className={rowClass}>
              {/* Track: dot + line */}
              <div className="atl-track">
                <div className="atl-dot">
                  {/* Completed check */}
                  {isCompleted && (
                    <svg className="atl-check" width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M2.5 6.5L5 9L9.5 3.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                  {/* Processing spinner ring */}
                  {isProcessing && <div className="atl-dot-spinner" />}
                  {/* Pulse ring on active */}
                  {isActive && <div className="atl-dot-pulse" />}
                </div>
                {/* Connecting line */}
                {i < APPROVAL_SEQUENCE.length - 1 && (
                  <div className="atl-line">
                    <div className={`atl-line-fill${isCompleted ? ' atl-line-fill--drawn' : ''}`} />
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="atl-content">
                <div className="atl-label">{step.label}</div>
                <div className="atl-desc">{step.desc}</div>
                {/* Reviewer chip — appears after processing */}
                {isCompleted && step.reviewer && (
                  <div className="atl-reviewer">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                    {step.reviewer}
                  </div>
                )}
                {/* Processing state */}
                {isProcessing && (
                  <div className="atl-processing">
                    <div className="atl-processing-bar"><div className="atl-processing-fill" /></div>
                    <span className="atl-processing-text">Verifying…</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer progress */}
      <div className="atl-footer">
        <div className="atl-progress">
          <div className="atl-progress-fill" style={{ width: `${progressPct}%` }} />
        </div>
        <span className={`atl-progress-label${showSuccess ? ' atl-progress-label--done' : ''}`}>
          {showSuccess ? 'Campaign is live' : `${progressPct}% complete`}
        </span>
      </div>
    </div>
  );
}

/* ─── Supporter Public View Preview ─────────────────────────── */
function SupporterPreview() {
  return (
    <div className="sv-browser-frame" aria-label="Supporter portal preview">
      {/* Browser bar */}
      <div className="db-browser-bar">
        <div className="db-browser-dots">
          <span className="db-dot db-dot--red" />
          <span className="db-dot db-dot--yellow" />
          <span className="db-dot db-dot--green" />
        </div>
        <div className="db-browser-address">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
          <span>greenfield-institute.bulandipe.com/campaigns</span>
        </div>
        <div className="db-browser-actions">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" aria-hidden="true"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"/></svg>
        </div>
      </div>

      {/* Portal shell */}
      <div className="sv-shell">
        {/* Top nav */}
        <div className="sv-topnav">
          <div className="sv-topnav-brand">
            <InstituteLogo />
            <div className="sv-brand-text">
              <div className="sv-brand-name">Greenfield Institute</div>
              <div className="sv-brand-sub">Supporter Portal</div>
            </div>
          </div>
          <div className="sv-user-chip">
            <div className="sv-user-avatar">SA</div>
            <span className="sv-user-name">Sana Ansari</span>
          </div>
        </div>

        {/* Hero strip */}
        <div className="sv-hero">
          <div className="sv-hero-content">
            <div className="sv-hero-eyebrow">
              <span className="sv-verified-dot" />
              Verified Student Campaigns
            </div>
            <h2 className="sv-hero-headline">Support verified students at Greenfield Institute</h2>
            <p className="sv-hero-sub">Every campaign is reviewed and approved by the institution. Browse student stories and contribute directly toward their goals.</p>
            <div className="sv-hero-stats">
              <div className="sv-hero-stat"><span className="sv-stat-val">109</span><span className="sv-stat-lbl">Supporters</span></div>
              <div className="sv-hero-stat"><span className="sv-stat-val">₹1.43L</span><span className="sv-stat-lbl">Raised</span></div>
              <div className="sv-hero-stat"><span className="sv-stat-val">Verified</span><span className="sv-stat-lbl">by Institution</span></div>
            </div>
            <button className="sv-browse-btn">View Campaigns</button>
          </div>
          <div className="sv-hero-illustration" aria-hidden="true">
            <img src={studentsIllustration} alt="" className="sv-hero-illustration-img" />
          </div>
        </div>

        {/* Tabs */}
        <div className="sv-tabs">
          <span className="sv-tab sv-tab--active">Browse Campaigns</span>
          <span className="sv-tab">My Contributions</span>
        </div>

        {/* Filters */}
        <div className="sv-filters">
          <div className="sv-search-bar">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" aria-hidden="true"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            <span className="sv-search-placeholder">Search by name, institution, or course…</span>
          </div>
          <div className="sv-filter-chips">
            <span className="sv-chip sv-chip--active">Most Recent</span>
            <span className="sv-chip">Ending Soon</span>
            <span className="sv-chip">Almost Funded</span>
          </div>
        </div>

        {/* Campaign grid */}
        <div className="sv-grid">
          {SUPPORTER_CAMPAIGNS.map((c, i) => (
            <div key={i} className="sv-card">
              <div className="sv-card-header">
                <div className="sv-avatar">{c.avatar}</div>
                <div className="sv-card-meta">
                  <div className="sv-card-name">{c.name}</div>
                  <div className="sv-card-batch">Batch {c.batch}</div>
                  <div className="sv-card-course">{c.course}</div>
                </div>
                <span className="sv-card-tag">Approved</span>
              </div>
              <div className="sv-card-title">{c.title}</div>
              <div className="sv-card-progress-row">
                <span className="sv-card-progress-label">₹{(c.raised/1000).toFixed(0)}k raised of ₹{(c.goal/1000).toFixed(0)}k</span>
                <span className="sv-card-pct">{c.pct}%</span>
              </div>
              <div className="sv-progress-track">
                <div className="sv-progress-fill" style={{ width: `${c.pct}%` }} />
              </div>
              <div className="sv-card-footer">
                <span className="sv-card-supporters">{c.supporters} supporters</span>
                <button className="sv-support-btn">Support Now</button>
              </div>
            </div>
          ))}
        </div>
        <div className="sv-disbursement-note">
          All contributions are received and managed by the institution on behalf of students.
        </div>
      </div>
    </div>
  );
}

/* ─── Student Portal Preview ─────────────────────────────────── */
function StudentPortalPreview() {
  return (
    <div className="sp-browser-frame" aria-label="Student portal preview">
      {/* Browser bar */}
      <div className="db-browser-bar">
        <div className="db-browser-dots">
          <span className="db-dot db-dot--red" />
          <span className="db-dot db-dot--yellow" />
          <span className="db-dot db-dot--green" />
        </div>
        <div className="db-browser-address">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
          <span>greenfield-institute.bulandipe.com/student</span>
        </div>
        <div className="db-browser-actions">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" aria-hidden="true"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"/></svg>
        </div>
      </div>

      {/* Blue institution header */}
      <div className="sp-topnav">
        <div className="sp-topnav-brand">
          <InstituteLogo />
          <div className="sp-brand-text">
            <div className="sp-brand-name">Greenfield Institute</div>
            <div className="sp-brand-sub">Student Portal</div>
          </div>
        </div>
        <div className="sp-topnav-user">
          <div className="sp-topnav-info">
            <span className="sp-topnav-name">Arjun Patel</span>
            <span className="sp-topnav-email">arjun.patel2024@gmail.com</span>
          </div>
          <div className="sp-topnav-avatar">AP</div>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.8)" strokeWidth="2.5" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>
        </div>
      </div>

      {/* Shell */}
      <div className="sp-shell">
        {/* Sidebar */}
        <aside className="sp-sidebar">
          <div className="sp-sidebar-profile">
            <div className="sp-profile-avatar">AP</div>
            <div className="sp-profile-info">
              <div className="sp-profile-name">Arjun Patel</div>
              <div className="sp-profile-id">31124U50037</div>
            </div>
          </div>
          <nav className="sp-sidebar-nav">
            {[
              { label: 'Dashboard', active: true, icon: 'M3 12l9-9 9 9M4 10v10h16V10' },
              { label: 'Profile', active: false, icon: 'M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8' },
            ].map((item, i) => (
              <div key={i} className={`sp-nav-item ${item.active ? 'sp-nav-item--active' : ''}`}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d={item.icon}/></svg>
                {item.label}
              </div>
            ))}
          </nav>
          <div className="sp-sidebar-signout">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9"/></svg>
            Sign Out
          </div>
        </aside>

        {/* Main */}
        <div className="sp-main">
          {/* Top bar */}
          <div className="sp-topbar">
            <div className="sp-breadcrumb">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" aria-hidden="true"><path d="M3 12l9-9 9 9M4 10v10h16V10"/></svg>
              <span className="sp-bc-sep">›</span>
              <span className="sp-bc-active">Student Dashboard</span>
            </div>
          </div>

          {/* Stat cards */}
          <div className="sp-stat-row">
            {[
              { label: 'Total Received', value: '₹97,000', icon: 'M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 11h.01M12 11h.01M15 11h.01M12 7v10' },
              { label: 'Active Campaigns', value: '1', icon: 'M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2' },
              { label: 'Total Campaigns', value: '2', icon: 'M4 6h16M4 10h16M4 14h16M4 18h16' },
            ].map((s, i) => (
              <div key={i} className="sp-stat-card">
                <div className="sp-stat-body">
                  <div className="sp-stat-label">{s.label}</div>
                  <div className="sp-stat-value">{s.value}</div>
                </div>
                <div className="sp-stat-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d={s.icon}/></svg>
                </div>
              </div>
            ))}
          </div>

          {/* Campaigns panel */}
          <div className="sp-campaigns-panel">
            <div className="sp-panel-header">
              <div>
                <span className="sp-panel-title">Campaigns</span>
                <span className="sp-panel-note">All funds go directly to your institution account</span>
              </div>
              <div className="sp-panel-actions">
                <div className="sp-filter-select">All Campaigns ▾</div>
                <button className="sp-request-btn">+ Request Campaign</button>
              </div>
            </div>
            {STUDENT_CAMPAIGNS_DATA.map((c, i) => (
              <div key={i} className="sp-campaign-row">
                <div className="sp-campaign-info">
                  <div className="sp-campaign-title">{c.title}</div>
                  <div className="sp-campaign-meta">Target: ₹{(c.target/1000).toFixed(0)},000 · {c.from} → {c.to}</div>
                  {c.pct > 0 && (
                    <>
                      <div className="sp-campaign-bar-track">
                        <div className="sp-campaign-bar-fill" style={{ width: `${c.pct}%` }} />
                      </div>
                      <div className="sp-campaign-raised">Raised: ₹{c.raised.toLocaleString('en-IN')} <span className="sp-campaign-pct-text">{c.pct}%</span></div>
                    </>
                  )}
                </div>
                <div className="sp-campaign-right">
                  <span className="sp-status-badge" style={{ color: c.color, background: `${c.color}18` }}>{c.status}</span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" aria-hidden="true"><path d="M9 18l6-6-6-6"/></svg>
                </div>
              </div>
            ))}
            <div className="sp-pagination">
              <button className="sp-page-btn">‹</button>
              <button className="sp-page-btn sp-page-btn--active">1</button>
              <button className="sp-page-btn">›</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Staff Management Preview ──────────────────────────────── */
const STAFF_ROWS = [
  { name: 'Priya Sharma',  email: 'priya.sharma@greenfield.ac.in',  roles: ['Institution Admin', 'Operations Manager'], status: 'Active' },
  { name: 'Kabir Nair',    email: 'kabir.nair@greenfield.ac.in',    roles: ['Staff Assistant'],                         status: 'Active' },
  { name: 'Rekha Iyer',    email: 'rekha.iyer@greenfield.ac.in',    roles: ['Operations Manager'],                      status: 'Invited' },
  { name: 'Sameer Verma',  email: 'sameer.verma@greenfield.ac.in',  roles: ['Institution Admin'],                       status: 'Active' },
  { name: 'Divya Menon',   email: 'divya.menon@greenfield.ac.in',   roles: ['Staff Assistant'],                         status: 'Active' },
  { name: 'Tariq Ansari',  email: 'tariq.ansari@greenfield.ac.in',  roles: ['Operations Manager', 'Staff Assistant'],   status: 'Active' },
];

const ROLE_COLORS = {
  'Institution Admin':  { bg: '#eff6ff', color: '#2563EB' },
  'Operations Manager': { bg: '#f0fdf4', color: '#16a34a' },
  'Staff Assistant':    { bg: '#faf5ff', color: '#7c3aed' },
};

const STATUS_COLORS = {
  'Active':   { bg: '#f0fdf4', color: '#16a34a' },
  'Invited':  { bg: '#fffbeb', color: '#d97706' },
  'Inactive': { bg: '#f9fafb', color: '#6b7280' },
};

function StaffManagementPreview() {
  return (
    <div className="sm-browser-frame" aria-label="Staff management interface preview">
      {/* macOS browser bar */}
      <div className="db-browser-bar">
        <div className="db-browser-dots">
          <span className="db-dot db-dot--red" />
          <span className="db-dot db-dot--yellow" />
          <span className="db-dot db-dot--green" />
        </div>
        <div className="db-browser-address">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
          <span>greenfield-institution.bulandipe.com/settings/users</span>
        </div>
        <div className="db-browser-actions">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" aria-hidden="true"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"/></svg>
        </div>
      </div>

      {/* Blue institution header */}
      <div className="sm-topnav">
        <div className="sm-topnav-brand">
          <InstituteLogo />
          <div className="sm-brand-text">
            <div className="sm-brand-name">Greenfield Institute</div>
            <div className="sm-brand-sub">Institution Portal</div>
          </div>
        </div>
        <div className="sm-topnav-user">
          <div className="sm-topnav-info">
            <span className="sm-topnav-name">Priya Sharma</span>
            <span className="sm-topnav-email">priya.sharma@greenfield.ac.in</span>
          </div>
          <div className="sm-topnav-avatar">PS</div>
        </div>
      </div>

      <div className="sm-shell">
        {/* Sidebar */}
        <aside className="sm-sidebar">
          <nav className="db-sidebar-nav">
            {[
              { label: 'Dashboard' },
              { label: 'Campaigns' },
              { label: 'Approval Requests' },
              { label: 'Plan & Usage' },
              { label: 'Reports', chevron: true },
              { label: 'Settings', chevron: true, active: true },
            ].map((item, i) => (
              <div key={i} className={`db-nav-item ${item.active ? 'db-nav-item--active' : ''}`}>
                {item.label}
                {item.chevron && <span className="db-nav-chevron">›</span>}
              </div>
            ))}
            <div className="sm-subnav">
              <div className="sm-subnav-item">Institution Settings</div>
              <div className="sm-subnav-item sm-subnav-item--active">Users</div>
            </div>
          </nav>
          <div className="db-sidebar-signout">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9"/></svg>
            Sign Out
          </div>
        </aside>

        {/* Main */}
        <div className="sm-main">
          {/* Breadcrumb bar */}
          <div className="sm-topbar">
            <div className="sm-breadcrumb">
              <span className="sm-bc-link">Dashboard</span>
              <span className="sm-bc-sep">›</span>
              <span className="sm-bc-active">Users</span>
            </div>
          </div>

          {/* Content card */}
          <div className="sm-content">
            <div className="sm-content-header">
              <div>
                <div className="sm-content-title">Institution Users</div>
                <div className="sm-content-sub">Manage staff access and roles</div>
              </div>
              <button className="sm-add-btn">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M19 8v6M22 11h-6"/></svg>
                Add User
              </button>
            </div>

            {/* Table */}
            <div className="sm-table">
              <div className="sm-table-head">
                <div className="sm-th">Name</div>
                <div className="sm-th">Email</div>
                <div className="sm-th">Role</div>
                <div className="sm-th">Status</div>
                <div className="sm-th sm-th--center">Actions</div>
              </div>
              {STAFF_ROWS.map((row, i) => (
                <div key={i} className="sm-table-row">
                  <div className="sm-td sm-td--name">{row.name}</div>
                  <div className="sm-td sm-td--email">{row.email}</div>
                  <div className="sm-td sm-td--roles">
                    {row.roles.map((r, j) => (
                      <span key={j} className="sm-role-badge" style={{ background: ROLE_COLORS[r].bg, color: ROLE_COLORS[r].color }}>
                        {r}
                      </span>
                    ))}
                  </div>
                  <div className="sm-td">
                    <span className="sm-status-pill" style={{ background: STATUS_COLORS[row.status].bg, color: STATUS_COLORS[row.status].color }}>
                      {row.status}
                    </span>
                  </div>
                  <div className="sm-td sm-td--actions">
                    <button className="sm-delete-btn" aria-label={`Remove ${row.name}`}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" aria-hidden="true"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Greenfield Institute SVG logo mark — Olympic torch / flame ─── */
function InstituteLogo() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
      {/* Dark green rounded square background */}
      <rect width="32" height="32" rx="8" fill="#15803d" />
      {/* Torch handle */}
      <rect x="14" y="20" width="4" height="8" rx="1.5" fill="white" opacity="0.9"/>
      {/* Torch cup / bowl */}
      <path d="M11 20 Q11 17 16 17 Q21 17 21 20 Z" fill="white" opacity="0.85"/>
      {/* Outer flame — amber/orange */}
      <path d="M16 3 C13 7 11 10 12 14 C13 17 16 17 16 17 C16 17 19 17 20 14 C21 10 19 7 16 3 Z" fill="#f97316" opacity="0.95"/>
      {/* Inner flame — yellow core */}
      <path d="M16 7 C15 9 14 11 15 13.5 C15.5 15.5 16 16 16 16 C16 16 16.5 15.5 17 13.5 C18 11 17 9 16 7 Z" fill="#fde047" opacity="0.95"/>
    </svg>
  );
}

/* ─── Sparkline: gradual upward trend, spike near end ─── */
/* 30 points: Feb 12 → Mar 3. Realistic spikes at: Feb 17 (~pt5), Feb 21 (~pt9), Feb 25 (~pt13), then big run-up near end */
const CHART_POINTS = [18, 22, 20, 25, 30, 65, 35, 28, 32, 70, 40, 34, 38, 80, 45, 38, 42, 48, 45, 50, 58, 78, 110, 145, 185, 215, 245, 218, 228, 232];

function MiniChart() {
  const w = 400, h = 80;
  const max = Math.max(...CHART_POINTS);
  const pts = CHART_POINTS.map((v, i) => {
    const x = (i / (CHART_POINTS.length - 1)) * w;
    const y = h - (v / max) * (h - 10) - 5;
    return `${x},${y}`;
  }).join(' ');
  const areaClose = `${w},${h} 0,${h}`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="db-chart-svg" aria-hidden="true" preserveAspectRatio="none">
      <defs>
        <linearGradient id="chartGradG" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#16a34a" stopOpacity="0.22" />
          <stop offset="100%" stopColor="#16a34a" stopOpacity="0" />
        </linearGradient>
      </defs>
      <polyline points={`${pts} ${areaClose}`} fill="url(#chartGradG)" stroke="none" />
      <polyline points={pts} fill="none" stroke="#16a34a" strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

const TOP_CAMPAIGNS = [
  { rank: 1, title: 'Support for academic and other expenses', raised: 420000, goal: 500000, supporters: 14, color: '#2563EB', status: 'Active' },
  { rank: 2, title: 'Tuition Assistance Request', raised: 275000, goal: 350000, supporters: 9, color: '#16a34a', status: 'Active' },
  { rank: 3, title: 'Help to finish the law course', raised: 190000, goal: 200000, supporters: 6, color: '#9333ea', status: 'Active' },
];

const RECENT_ACTIVITIES = [
  { label: 'Campaign approved & published', sub: 'Admin approved "Support for academic expenses" — now live for supporters', time: '07/03/2026, 9:41 am' },
  { label: 'New campaign submitted', sub: 'Priya Menon submitted "Tuition Assistance Request" for institutional review', time: '06/03/2026, 7:09 pm' },
  { label: 'Supporter contribution received', sub: '₹25,000 contributed toward "Help to finish the law course"', time: '06/03/2026, 12:54 pm' },
];

function DashboardPreview() {
  return (
    /* ── macOS Safari browser chrome ── */
    <div className="db-browser-frame" aria-label="Institution dashboard preview">
      <div className="db-browser-bar">
        <div className="db-browser-dots">
          <span className="db-dot db-dot--red" />
          <span className="db-dot db-dot--yellow" />
          <span className="db-dot db-dot--green" />
        </div>
        <div className="db-browser-address">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
          <span>greenfield-institution.bulandipe.com/dashboard</span>
        </div>
        <div className="db-browser-actions">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" aria-hidden="true"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"/></svg>
        </div>
      </div>

      {/* ── Dashboard shell ── */}
      <div className="db-shell">
        {/* Sidebar */}
        <aside className="db-sidebar">
          <div className="db-sidebar-header">
            <div className="db-sidebar-brand">
              <InstituteLogo />
              <div className="db-brand-text">
                <div className="db-brand-name">Greenfield Institute</div>
                <div className="db-brand-sub">Institution Portal</div>
              </div>
            </div>
          </div>
          <nav className="db-sidebar-nav">
            {[
              { label: 'Dashboard', active: true },
              { label: 'Campaigns', active: false },
              { label: 'Approval Requests', active: false },
              { label: 'Plan & Usage', active: false },
              { label: 'Reports', chevron: true, active: false },
              { label: 'Settings', chevron: true, active: false },
            ].map((item, i) => (
              <div key={i} className={`db-nav-item ${item.active ? 'db-nav-item--active' : ''}`}>
                {item.label}
                {item.chevron && <span className="db-nav-chevron">›</span>}
              </div>
            ))}
          </nav>
          <div className="db-sidebar-signout">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9"/></svg>
            Sign Out
          </div>
        </aside>

        {/* Main */}
        <div className="db-main">
          {/* Topbar */}
          <div className="db-topbar">
            <h1 className="db-page-title">Dashboard Overview</h1>
            <div className="db-topbar-user">
              <div className="db-user-info">
                <span className="db-user-name">Rahul Sharma</span>
                <span className="db-user-email">admin@greenfield.ac.in</span>
              </div>
              <div className="db-user-avatar">RS</div>
            </div>
          </div>

          {/* Stat cards — 3-col grid, contained */}
          <div className="db-stat-row">
            {[
              { label: 'Pending Approvals', value: '6', bg: '#fffbeb', iconBg: '#f59e0b' },
              { label: 'Active Campaigns', value: '57', bg: '#f0fdf4', iconBg: '#16a34a' },
              { label: 'Funds Raised', value: '₹16,00,000', bg: '#fff', iconBg: '#6366f1' },
            ].map((s, i) => (
              <div key={i} className="db-stat-card" style={{ background: s.bg }}>
                <div className="db-stat-body">
                  <div className="db-stat-label">{s.label}</div>
                  <div className="db-stat-value">{s.value}</div>
                </div>
                <div className="db-stat-icon" style={{ background: `${s.iconBg}1a`, color: s.iconBg }}>
                  {i === 0 && <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><rect x="9" y="2" width="6" height="4" rx="1"/><rect x="3" y="6" width="18" height="14" rx="2"/><path d="M9 12h6M9 16h4"/></svg>}
                  {i === 1 && <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M8 12l3 3 5-5"/></svg>}
                  {i === 2 && <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><rect x="2" y="6" width="20" height="13" rx="2"/><path d="M2 10h20"/></svg>}
                </div>
              </div>
            ))}
          </div>

          {/* Middle row */}
          <div className="db-mid-row">
            {/* Funding Overview chart */}
            <div className="db-chart-card">
              <div className="db-chart-header">
                <span className="db-chart-title">Funding Overview</span>
                <div className="db-chart-tabs">
                  <span className="db-chart-tab">Last 7 Days</span>
                  <span className="db-chart-tab db-chart-tab--active">Last 20 Days</span>
                </div>
              </div>
              <div className="db-chart-meta">
                <div>
                  <div className="db-chart-meta-label">Contributions (Last 20 Days)</div>
                  <div className="db-chart-meta-value db-chart-meta-value--green">₹12,40,000</div>
                </div>
                <div>
                  <div className="db-chart-meta-label">Best Day</div>
                  <div className="db-chart-meta-value db-chart-meta-value--green">₹3,75,000</div>
                </div>
              </div>
              <div className="db-chart-wrap">
                <div className="db-chart-yaxis">
                  {['₹4.0L', '₹3.0L', '₹2.0L', '₹1.0L', '₹0'].map((l, i) => (
                    <span key={i} className="db-chart-ylabel">{l}</span>
                  ))}
                </div>
                <div className="db-chart-area">
                  <MiniChart />
                  <div className="db-chart-xaxis">
                    {['12 Feb','15 Feb','17 Feb','19 Feb','21 Feb','23 Feb','25 Feb','27 Feb','01 Mar','03 Mar'].map((l, i) => (
                      <span key={i} className="db-chart-xlabel">{l}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Top Campaigns */}
            <div className="db-top-campaigns">
              <div className="db-section-title">Top Campaigns</div>
              {TOP_CAMPAIGNS.map((c, i) => (
                <div key={i} className="db-campaign-row">
                  <div className="db-campaign-rank" style={{ color: c.color }}>#{c.rank}</div>
                  <div className="db-campaign-info">
                    <div className="db-campaign-title">{c.title}</div>
                    <div className="db-campaign-sub">
                      ₹{c.raised.toLocaleString('en-IN')} raised of ₹{c.goal.toLocaleString('en-IN')}
                    </div>
                    <div className="db-campaign-bar-track">
                      <div className="db-campaign-bar-fill" style={{ width: `${Math.round((c.raised / c.goal) * 100)}%`, background: c.color }} />
                    </div>
                    <div className="db-campaign-helpers">{c.supporters} supporters helped</div>
                  </div>
                  <span className="db-campaign-status">{c.status}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Activities */}
          <div className="db-activity-card">
            <div className="db-activity-header">
              <span className="db-section-title">Recent Activities</span>
              <div className="db-activity-search">
                <input className="db-search-input" placeholder="Search activities" aria-label="Search activities" readOnly />
                <button className="db-search-btn">Search</button>
                <button className="db-refresh-btn">Refresh</button>
              </div>
            </div>
            {RECENT_ACTIVITIES.map((a, i) => (
              <div key={i} className="db-activity-row">
                <div className="db-activity-icon" aria-hidden="true">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
                </div>
                <div className="db-activity-body">
                  <div className="db-activity-label">{a.label}</div>
                  <div className="db-activity-sub">{a.sub}</div>
                </div>
                <div className="db-activity-time">{a.time}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── FAQ ───────────────────────────────────────────────────────── */
const FAQ_ITEMS = [
  {
    q: 'What exactly is BulandiPe?',
    a: 'BulandiPe is a platform that allows educational institutions to manage student fundraising in a structured, transparent way. Students submit funding requests through your institution\'s portal. Your team reviews and approves each one. Only approved campaigns are made visible to supporters, the people who can browse and contribute to student campaigns.'
  },
  {
    q: 'How does the money actually get to the student?',
    a: 'Contributions go directly to the institution\'s account. From there, the institution applies the funds toward the student\'s specific need, whether that is tuition, accommodation, or whatever the campaign was created for. Donors know exactly what they are supporting, and students receive help in a structured, dignified way. The institution stays in control throughout.'
  },
  {
    q: 'Who controls what gets published publicly?',
    a: 'Your institution does. No campaign appears on your institution\'s supporter page, where members of the public can browse campaigns and contribute, without being reviewed and approved by a designated staff member. You decide who on your team has that authority, and every approval is logged.'
  },
  {
    q: 'What does the Trial plan include, and is it really free?',
    a: 'Yes, the Trial plan is free to get started. It gives your institution access to the core platform: student campaigns, institutional review, and a public-facing page. You can explore how the platform works with real campaigns before committing to a paid plan.'
  },
  {
    q: 'Does everyone get their own place to log in?',
    a: 'Yes. Your institution gets its own dashboard to manage students, campaigns, and approvals. Students get their own login to submit campaigns and track funding progress. Supporters get a separate page to browse your institution\'s campaigns and contribute. Each has their own space, nothing is shared or mixed between them.'
  },
  {
    q: 'How long does it take to get started?',
    a: 'Submit your details through the form above and our team will be in touch within one business day to walk you through setup. There is no technical work required on your end.'
  },
];

/* Generated directly from FAQ_ITEMS so the schema can never drift from the
   visible on-page text (Google requires them to match word for word). */
const FAQ_SCHEMA_JSON = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ_ITEMS.map((item) => ({
    '@type': 'Question',
    name: item.q,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.a,
    },
  })),
}).replace(/</g, '\\u003c');

function FAQSection() {
  const [openIndex, setOpenIndex] = useState(null);
  const toggle = (i) => {
    if (openIndex !== i) trackFAQExpand(FAQ_ITEMS[i].q);
    setOpenIndex(openIndex === i ? null : i);
  };
  return (
    <section id="faq" className="bp-section bp-faq-section">
      <div className="bp-section-inner">
        <div className="bp-section-lede">
          <div className="bp-eyebrow">FAQ</div>
          <h2 className="bp-section-headline">Questions we hear often.</h2>
          <p className="bp-section-sub">If something is still unclear after reading these, just reach out. We are happy to walk you through it.</p>
        </div>
        <div className="bp-faq-list">
          {FAQ_ITEMS.map((item, i) => (
            <div key={i} className={`bp-faq-item${openIndex === i ? ' bp-faq-item--open' : ''}`}>
              <button className="bp-faq-q" onClick={() => toggle(i)} aria-expanded={openIndex === i}>
                <span>{item.q}</span>
                <span className="bp-faq-icon" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M6 9l6 6 6-6"/></svg>
                </span>
              </button>
              {openIndex === i && (
                <div className="bp-faq-a">{item.a}</div>
              )}
            </div>
          ))}
        </div>
      </div>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: FAQ_SCHEMA_JSON }} />
    </section>
  );
}

// CountryCodePicker imported from ./components/CountryCodePicker
const CountryCodePicker = CountryCodePickerComponent;

/* ─── Main App ─────────────────────────────────────────────────── */
function App() {
  const [activeSection, setActiveSection] = useState('home');
  const [scrolled, setScrolled] = useState(false);
  const [contactForm, setContactForm] = useState({ institutionName: '', contactPerson: '', email: '', phone: '', message: '' });
  const [contactCountryCode, setContactCountryCode] = useState('+91');
  const [collaborationForm, setCollaborationForm] = useState({ institutionName: '', contactPerson: '', email: '', phone: '', message: '' });
  const [collabCountryCode, setCollabCountryCode] = useState('+91');
  const [demoForm, setDemoForm] = useState({ institutionName: '', contactPerson: '', email: '', phone: '', preferredDate: '', message: '' });
  const [contactTurnstileToken, setContactTurnstileToken] = useState(null);
  const [collabTurnstileToken, setCollabTurnstileToken] = useState(null);
  const [demoTurnstileToken, setDemoTurnstileToken] = useState(null);
  const [demoCountryCode, setDemoCountryCode] = useState('+91');
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  const [collabStatus, setCollabStatus] = useState({ type: '', message: '' });
  const [demoStatus, setDemoStatus] = useState({ type: '', message: '' });
  const [successModal, setSuccessModal] = useState({ open: false, institutionName: '', variant: 'demo' });
  const successModalTimerRef = useRef(null);
  const [successModalProgress, setSuccessModalProgress] = useState(100);
  const [planModal, setPlanModal] = useState({ open: false, plan: 'Trial' });
  const [legalModal, setLegalModal] = useState({ open: false, doc: 'privacy', section: null });
  const [cookieConsent, setCookieConsent] = useState(getConsent());

  // form_start dedup flags — useRef so they don't trigger re-renders
  const contactFormStarted = useRef(false);
  const collabFormStarted = useRef(false);
  const demoFormStarted = useRef(false);

  useEffect(() => {
    initAnalyticsFromConsent();
  }, []);

  const handleCookieAccept = () => {
    setConsent('accepted');
    loadGA();
    setCookieConsent('accepted');
  };

  const handleCookieDecline = () => {
    setConsent('declined');
    setCookieConsent('declined');
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
      const sections = ['home', 'problem', 'product', 'how-it-works', 'institutions', 'demo', 'contact'];
      const current = sections.find(id => {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          return rect.top <= 120 && rect.bottom >= 120;
        }
        return false;
      });
      if (current) {
        setActiveSection(prev => {
          if (prev !== current) {
            pageview(`/#${current}`);
            trackSectionView(current);
          }
          return current;
        });
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const closeSuccessModal = () => {
    clearInterval(successModalTimerRef.current);
    setSuccessModal({ open: false, institutionName: '', variant: 'demo' });
    setSuccessModalProgress(100);
  };

  const openSuccessModal = (institutionName, variant) => {
    setSuccessModalProgress(100);
    setSuccessModal({ open: true, institutionName, variant });
    const totalMs = 20000;
    const tickMs = 100;
    let elapsed = 0;
    successModalTimerRef.current = setInterval(() => {
      elapsed += tickMs;
      setSuccessModalProgress(Math.max(0, 100 - (elapsed / totalMs) * 100));
      if (elapsed >= totalMs) closeSuccessModal();
    }, tickMs);
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactTurnstileToken) {
      setSubmitStatus({ type: 'error', message: 'Please complete the verification challenge.' });
      return;
    }
    setSubmitStatus({ type: 'loading', message: 'Submitting…' });
    try {
      const institutionName = contactForm.institutionName;
      await axios.post(`${API_URL}/api/public/contact`, { ...contactForm, turnstileToken: contactTurnstileToken });
      setSubmitStatus({ type: '', message: '' });
      setContactForm({ institutionName: '', contactPerson: '', email: '', phone: '', message: '' });
      contactFormStarted.current = false;
      trackContactSubmit(institutionName);
      openSuccessModal(institutionName, 'contact');
    } catch (err) {
      trackFormError('contact', 'api_error');
      setSubmitStatus({ type: 'error', message: err.response?.data?.error || 'Failed to submit. Please try again.' });
    }
  };

  const handleCollaborationSubmit = async (e) => {
    e.preventDefault();
    if (!collabTurnstileToken) {
      setCollabStatus({ type: 'error', message: 'Please complete the verification challenge.' });
      return;
    }
    setCollabStatus({ type: 'loading', message: 'Submitting…' });
    try {
      const institutionName = collaborationForm.institutionName;
      await axios.post(`${API_URL}/api/public/partnership`, { ...collaborationForm, turnstileToken: collabTurnstileToken });
      setCollabStatus({ type: '', message: '' });
      setCollaborationForm({ institutionName: '', contactPerson: '', email: '', phone: '', message: '' });
      collabFormStarted.current = false;
      trackPartnershipApplication(institutionName);
      openSuccessModal(institutionName, 'partnership');
    } catch (err) {
      trackFormError('partnership', 'api_error');
      setCollabStatus({ type: 'error', message: err.response?.data?.error || 'Failed to submit. Please try again.' });
    }
  };

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    if (!demoTurnstileToken) {
      setDemoStatus({ type: 'error', message: 'Please complete the verification challenge.' });
      return;
    }
    setDemoStatus({ type: 'loading', message: 'Submitting…' });
    try {
      const institutionName = demoForm.institutionName;
      await axios.post(`${API_URL}/api/public/demo`, { ...demoForm, turnstileToken: demoTurnstileToken });
      setDemoStatus({ type: '', message: '' });
      setDemoForm({ institutionName: '', contactPerson: '', email: '', phone: '', preferredDate: '', message: '' });
      demoFormStarted.current = false;
      trackDemoRequest(institutionName);
      openSuccessModal(institutionName, 'demo');
    } catch (err) {
      trackFormError('demo', 'api_error');
      setDemoStatus({ type: 'error', message: err.response?.data?.error || 'Failed to submit. Please try again.' });
    }
  };

  const handlePlanInterestSubmit = async (data) => {
    await axios.post(`${API_URL}/api/public/plan-interest`, {
      institutionName: data.institutionName,
      contactPerson: data.contactPerson,
      email: data.email,
      phone: data.phone,
      selectedPlan: data.selectedPlan,
      message: data.message || undefined,
      turnstileToken: data.turnstileToken,
    });
  };

  const navLinks = NAV_LINKS;

  return (
    <Router>
      <Routes>
        <Route path="/help" element={<HelpPage />} />
        <Route path="/*" element={<LandingPage />} />
      </Routes>
    </Router>
  );

  function LandingPage() {
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    });
  }, [location.hash]);

  return (
    <>
      <div className="bp-root">

        {/* ── Navigation ── */}
        <SiteNav
          navLinks={navLinks}
          activeId={activeSection}
          forceSolid={scrolled}
          onScrollLinkClick={scrollTo}
          onContactClick={() => { trackCTAClick('nav_contact_us'); scrollTo('contact'); }}
        />

        {/* ── 01 HERO ── */}
        <section id="home" className="bp-hero" data-testid="hero-section">
          <div className="bp-hero-inner">
            {/* Centered copy block */}
            <div className="bp-hero-copy">
              <div className="bp-eyebrow">Built for institutions that put their students first</div>
              <h1 className="bp-hero-headline" data-testid="hero-heading">
                Give your students a structured way to raise funds, under your oversight.
              </h1>
              <p className="bp-hero-sub" data-testid="hero-description">
                BulandiPe is a platform built for institutions. Students launch campaigns within your system. You review, approve, and control what supporters ever see.
              </p>
              <div className="bp-hero-actions">
                <button onClick={() => { trackCTAClick('hero_partnership'); scrollTo('institutions'); }} className="bp-btn-primary" data-testid="hero-cta-explore">
                  Start an Institution Partnership <ArrowRight size={16} />
                </button>
                <button onClick={() => { trackCTAClick('hero_how_it_works'); scrollTo('how-it-works'); }} className="bp-btn-ghost" data-testid="hero-cta-collaborate">
                  See how it works
                </button>
              </div>
            </div>

            {/* Dashboard screenshot — existing component, centered below copy */}
            <div className="bp-hero-dashboard-wrap">
              <DashboardPreview />
              <div className="bp-hero-dashboard-veil" aria-hidden="true" />
            </div>
          </div>
        </section>

        {/* ── 02 THE PROBLEM ── */}
        <section id="problem" className="bp-section" data-testid="why-section">
          <div className="bp-section-inner">
            <div className="bp-section-lede">
              <div className="bp-eyebrow">The Problem</div>
              <h2 className="bp-section-headline" data-testid="why-heading">
                Supporting students shouldn't be complicated.
              </h2>
              <p className="bp-section-sub" data-testid="why-description">
                Many students need financial support to continue their education, but institutions often lack a structured way to manage it. A student posts a fundraising link publicly and your institution finds out weeks later. Requests happen informally, campaigns appear across different platforms, and you lose visibility into what is being shared in your name.
              </p>
            </div>

            <div className="bp-problem-grid">
              <div className="bp-problem-item">
                <div className="bp-problem-number">01</div>
                <h3 className="bp-problem-title">Unstructured fundraising</h3>
                <p className="bp-problem-desc">Students often turn to external platforms to raise funds, leaving institutions unaware of what is being shared publicly.</p>
              </div>
              <div className="bp-problem-item">
                <div className="bp-problem-number">02</div>
                <h3 className="bp-problem-title">No institutional oversight</h3>
                <p className="bp-problem-desc">Without a structured process, institutions cannot easily review campaigns or ensure that fundraising is responsible and transparent.</p>
              </div>
              <div className="bp-problem-item">
                <div className="bp-problem-number">03</div>
                <h3 className="bp-problem-title">Disconnected student support</h3>
                <p className="bp-problem-desc">Students need support, but institutions lack a centralized system to guide campaigns and track their outcomes.</p>
              </div>
            </div>
          </div>
        </section>

        {/* ── 03 THE PRODUCT ── */}
        <section id="product" className="bp-section bp-section--alt" data-testid="product-section">
          <div className="bp-section-inner">
            <div className="bp-section-lede">
              <div className="bp-eyebrow">The Platform</div>
              <h2 className="bp-section-headline">
                A better way to support student fundraising.
              </h2>
              <p className="bp-section-sub">
                Start by setting up your team and their roles. Students then operate within that structure, and what supporters see is only what you have approved.
              </p>
            </div>

            {/* ── Block 1: Staff & Roles — bleed right ── */}
            <div className="bp-showcase-block">
              <div className="bp-showcase-text">
                <div className="bp-eyebrow">Staff &amp; Roles</div>
                <h3 className="bp-product-title">The right people in the right roles.</h3>
                <p className="bp-product-desc">
                  Manage who on your team can review campaigns, approve funding requests, and access institutional data, without needing to involve IT or external tools.
                </p>
                <ul className="bp-product-list">
                  <li>Role-based access for every team member</li>
                  <li>Assign multiple roles per staff member</li>
                  <li>Invite new members or deactivate access instantly</li>
                  <li>Clear record of every action taken</li>
                </ul>
              </div>
              <div className="bp-showcase-ui bp-showcase-ui--bleed-right">
                <StaffManagementPreview />
              </div>
            </div>

            {/* ── Block 2: Approval Workflow ── */}
            <div className="bp-product-block bp-product-block--reverse">
              <div className="bp-product-text">
                <div className="bp-eyebrow">Approval Workflow</div>
                <h3 className="bp-product-title">Every campaign reviewed before supporters can see it.</h3>
                <p className="bp-product-desc">
                  Before a campaign reaches supporters, it moves through a clear institutional review process. Your team verifies the details, approves the request, and controls when it is published.
                </p>
                <ul className="bp-product-list">
                  <li>Defined multi-stage review process</li>
                  <li>Approvals assigned by role</li>
                  <li>Nothing published without institutional sign-off</li>
                  <li>Automated notifications at every step</li>
                </ul>
              </div>
              <div className="bp-product-ui">
                <ApprovalTimeline />
              </div>
            </div>

            {/* ── Block 3: Student Portal — bleed left ── */}
            <div className="bp-showcase-block bp-showcase-block--reverse">
              <div className="bp-showcase-ui bp-showcase-ui--bleed-left">
                <StudentPortalPreview />
              </div>
              <div className="bp-showcase-text">
                <div className="bp-eyebrow">Student Portal</div>
                <h3 className="bp-product-title">Students share their journey. Your institution provides the platform.</h3>
                <p className="bp-product-desc">
                  Students can create campaigns directly within the institution's platform, allowing their stories to be shared responsibly while maintaining institutional credibility.
                </p>
                <ul className="bp-product-list">
                  <li>Campaign creation and submission within the platform</li>
                  <li>Real-time visibility into funding progress</li>
                  <li>Status updates at every stage of review</li>
                  <li>Full record of contributions received</li>
                </ul>
              </div>
            </div>

            {/* ── Block 4: Supporter Experience — bleed right ── */}
            <div className="bp-showcase-block">
              <div className="bp-showcase-text">
                <div className="bp-eyebrow">Supporter Experience</div>
                <h3 className="bp-product-title">A trusted place for supporters to contribute.</h3>
                <p className="bp-product-desc">
                  Every institution receives a dedicated supporter page where approved campaigns are displayed. Supporters can explore student stories, follow progress, and contribute with confidence.
                </p>
                <ul className="bp-product-list">
                  <li>Dedicated institution-branded public URL</li>
                  <li>Only institution-approved campaigns are shown</li>
                  <li>Clear student context and funding progress</li>
                  <li>Simple, trustworthy contribution experience</li>
                </ul>
              </div>
              <div className="bp-showcase-ui bp-showcase-ui--bleed-right">
                <SupporterPreview />
              </div>
            </div>
          </div>
        </section>

        {/* ── 04 HOW IT WORKS ── */}
        <section id="how-it-works" className="bp-section" data-testid="how-it-works-section">
          <div className="bp-section-inner">
            <div className="bp-section-lede">
              <div className="bp-eyebrow">How It Works</div>
              <h2 className="bp-section-headline" data-testid="how-heading">
                Up and running in four steps.
              </h2>
            </div>

            <div className="bp-steps">
              {[
                {
                  n: '01',
                  title: 'Your institution sets the rules',
                  desc: 'Configure who on your team can create campaigns and who is responsible for reviewing and approving them before anything goes public.',
                },
                {
                  n: '02',
                  title: 'Students apply within your system',
                  desc: 'Students submit funding requests through the platform. Your team reviews eligibility and approves each campaign.',
                },
                {
                  n: '03',
                  title: 'Supporters give with confidence',
                  desc: 'Only approved campaigns appear on your institution\'s supporter page. Supporters see verified stories and give directly.',
                },
                {
                  n: '04',
                  title: 'You see the full picture',
                  desc: 'Track every campaign, every contribution, and every outcome from one dashboard. Reports are ready when you need them.',
                },
              ].map((step, i) => (
                <div key={i} className="bp-step" data-testid={`step-${i + 1}`}>
                  <div className="bp-step-number">{step.n}</div>
                  <div className="bp-step-body">
                    <h3 className="bp-step-title">{step.title}</h3>
                    <p className="bp-step-desc">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── 05 BUILT FOR INSTITUTIONS ── */}
        <section id="institutions" className="bp-section bp-section--dark" data-testid="institutions-section">
          <div className="bp-section-inner">
            <div className="bp-section-lede bp-section-lede--light">
              <div className="bp-eyebrow bp-eyebrow--light">For Modern Institutions</div>
              <h2 className="bp-section-headline" data-testid="institutions-heading">
                Institutions that care about students deserve better tools.
              </h2>
              <p className="bp-section-sub" data-testid="institutions-description">
                Most institutions want to support their students. The problem is never the intention. It is the absence of a system that makes support structured, visible, and accountable. BulandiPe is that system.
              </p>
            </div>

            <div className="bp-capabilities">
              {[
                { title: 'Campaign controls', desc: 'Define who on your team can create campaigns, who reviews them, and who gives final approval before a campaign goes live.' },
                { title: 'Student verification', desc: 'Academic standing, enrollment status, and financial eligibility are validated through your institution\'s own records.' },
                { title: 'Funding transparency', desc: 'Every contribution is visible to institutional administrators, with transaction logs available for compliance reporting.' },
                { title: 'Progress tracking', desc: 'Monitor active campaigns in real-time. Get notified when milestones are reached or when campaigns require attention.' },
                { title: 'Outcome reporting', desc: 'Generate structured reports on campaign outcomes, funds received, and student progress, ready for board or accreditation review.' },
                { title: 'Structured partnerships', desc: 'Build formal relationships with corporate donors, alumni networks, and foundations through structured partnership agreements.' },
              ].map((cap, i) => (
                <div key={i} className="bp-capability">
                  <div className="bp-capability-num">{String(i + 1).padStart(2, '0')}</div>
                  <h3 className="bp-capability-title">{cap.title}</h3>
                  <p className="bp-capability-desc">{cap.desc}</p>
                </div>
              ))}
            </div>

            {/* Partnership form */}
            <div className="bp-partnership-block">
              <div className="bp-partnership-text">
                <h3 className="bp-partnership-headline">Apply for institutional partnership</h3>
                <p className="bp-partnership-desc">
                  We work directly with institutions to configure the platform around your specific requirements. Submit your details and we will be in touch within one business day.
                </p>
                <div className="bp-partnership-facts">
                  <div className="bp-fact"><CheckCircle size={16} /> Dedicated onboarding support</div>
                  <div className="bp-fact"><CheckCircle size={16} /> Platform configured around your requirements</div>
                  <div className="bp-fact"><CheckCircle size={16} /> No platform fees to get started</div>
                </div>
              </div>

              <form className="bp-form" onSubmit={handleCollaborationSubmit} data-testid="collaboration-form">
                {collabStatus.message && (
                  <div className={`bp-form-status bp-form-status--${collabStatus.type}`} data-testid="collab-status-message">
                    {collabStatus.type === 'success' && <CheckCircle size={16} />}
                    {collabStatus.message}
                  </div>
                )}
                <div className="bp-form-row">
                  <div className="bp-form-field">
                    <label htmlFor="ci-institution">Institution Name *</label>
                    <input id="ci-institution" type="text" required placeholder="Your Institution" value={collaborationForm.institutionName} onChange={e => { if (!collabFormStarted.current) { collabFormStarted.current = true; trackFormStart('partnership'); } setCollaborationForm({ ...collaborationForm, institutionName: e.target.value }); }} data-testid="collab-input-institution" />
                  </div>
                  <div className="bp-form-field">
                    <label htmlFor="ci-person">Contact Person *</label>
                    <input id="ci-person" type="text" required placeholder="Your Name" value={collaborationForm.contactPerson} onChange={e => { if (!collabFormStarted.current) { collabFormStarted.current = true; trackFormStart('partnership'); } setCollaborationForm({ ...collaborationForm, contactPerson: e.target.value }); }} data-testid="collab-input-person" />
                  </div>
                </div>
                <div className="bp-form-row">
                  <div className="bp-form-field">
                    <label htmlFor="ci-email">Email *</label>
                    <input id="ci-email" type="email" required placeholder="you@institution.edu.in" value={collaborationForm.email} onChange={e => { if (!collabFormStarted.current) { collabFormStarted.current = true; trackFormStart('partnership'); } setCollaborationForm({ ...collaborationForm, email: e.target.value }); }} data-testid="collab-input-email" />
                  </div>
                  <div className="bp-form-field">
                    <label htmlFor="ci-phone">Phone</label>
                    <div className="bp-phone-field">
                      <CountryCodePicker value={collabCountryCode} onChange={setCollabCountryCode} dark={true} />
                      <input id="ci-phone" type="tel" placeholder="XXXXX XXXXX" maxLength={10} pattern="[0-9]{10}" title="Enter a valid 10-digit phone number" value={collaborationForm.phone} onChange={e => { if (!collabFormStarted.current) { collabFormStarted.current = true; trackFormStart('partnership'); } setCollaborationForm({ ...collaborationForm, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }); }} data-testid="collab-input-phone" />
                    </div>
                  </div>
                </div>
                <div className="bp-form-field">
                  <label htmlFor="ci-message">Message</label>
                  <textarea id="ci-message" rows={4} placeholder="Tell us about your institution and what you're looking to achieve." value={collaborationForm.message} onChange={e => { if (!collabFormStarted.current) { collabFormStarted.current = true; trackFormStart('partnership'); } setCollaborationForm({ ...collaborationForm, message: e.target.value }); }} data-testid="collab-input-message" />
                </div>
                <TurnstileWidget onVerify={setCollabTurnstileToken} onExpire={() => setCollabTurnstileToken(null)} />
                <button type="submit" className="bp-btn-primary bp-btn-form" disabled={collabStatus.type === 'loading' || !collabTurnstileToken} data-testid="collab-submit-button">
                  {collabStatus.type === 'loading' ? 'Submitting…' : 'Apply for Partnership'}
                  <Send size={15} />
                </button>
              </form>
            </div>
          </div>
        </section>

        {/* ── 06 PRICING ── */}
        <PricingSection onGetStarted={(plan) => setPlanModal({ open: true, plan })} />

        {/* ── 07 TRUST & PROOF ── */}
        <section className="bp-section" data-testid="trust-section">
          <div className="bp-section-inner">
            <div className="bp-section-lede">
              <div className="bp-eyebrow">Scale & Impact</div>
              <h2 className="bp-section-headline">
                Infrastructure built for the long term.
              </h2>
              <p className="bp-section-sub">
                BulandiPe is designed for institutions that take student welfare seriously. Not as a one-time initiative, but as an ongoing commitment embedded in how they operate.
              </p>
            </div>

            <div className="bp-trust-metrics">
              {[
                { value: '3', label: 'Dedicated portals for institutions, students and supporters' },
                { value: '100%', label: 'Institution-controlled. Every campaign reviewed before it goes live' },
                { value: '₹0', label: 'Cost to get started on the Trial plan' },
                { value: 'Scalable', label: 'Plans built to grow with your institution as you need more' },
              ].map((m, i) => (
                <div key={i} className="bp-trust-metric">
                  <div className="bp-trust-value">{m.value}</div>
                  <div className="bp-trust-label">{m.label}</div>
                </div>
              ))}
            </div>

            <div className="bp-trust-statement">
              <blockquote className="bp-blockquote">
                "Institutions that support students responsibly don't just change individual outcomes. They build the kind of reputation that attracts better students, better faculty, and better partners."
              </blockquote>
              <div className="bp-blockquote-attr">BulandiPe, on why structure matters</div>
            </div>
          </div>
        </section>

        {/* ── 08 FAQ ── */}
        <FAQSection />

        {/* ── 09 FINAL CTA ── */}
        <section className="bp-section bp-section--cta" data-testid="cta-section">
          <div className="bp-cta-inner">
            <div className="bp-eyebrow bp-eyebrow--light">Get Started</div>
            <h2 className="bp-cta-headline">
              Set up your institution's platform today.
            </h2>
            <p className="bp-cta-sub">
              We configure everything around your institution's requirements. Your team is supported from day one, and your students have a proper system to work within.
            </p>
            <div className="bp-cta-actions">
              <button onClick={() => { trackCTAClick('apply_for_partnership'); scrollTo('institutions'); }} className="bp-btn-cta-primary">
                Apply for Partnership <ArrowRight size={16} />
              </button>
              <button onClick={() => { trackCTAClick('book_a_demo'); scrollTo('demo'); }} className="bp-btn-cta-ghost">
                Book a Demo
              </button>
            </div>
          </div>
        </section>

        {/* ── DEMO REQUEST ── */}
        <section id="demo" className="bp-section bp-section--alt" data-testid="demo-section">
          <div className="bp-section-inner">
            <div className="bp-section-lede">
              <div className="bp-eyebrow">Book a Demo</div>
              <h2 className="bp-section-headline" data-testid="demo-heading">See the platform in action</h2>
              <p className="bp-section-sub" data-testid="demo-description">
                Schedule a personalised walkthrough with our team. We'll show you exactly how BulandiPe works for your institution.
              </p>
            </div>

            <div className="bp-contact-layout">
              <div className="bp-contact-info">
                <div className="bp-contact-detail">
                  <div className="bp-contact-note">
                    Sessions are typically 30 minutes. We'll walk through the institution dashboard, student portal, and supporter experience.
                  </div>
                </div>
                <div className="bp-demo-facts">
                  <div className="bp-demo-fact"><CheckCircle size={16} /> Live walkthrough of all three portals</div>
                  <div className="bp-demo-fact"><CheckCircle size={16} /> Q&amp;A with our team</div>
                  <div className="bp-demo-fact"><CheckCircle size={16} /> No commitment required</div>
                </div>
              </div>

              <form className="bp-form" onSubmit={handleDemoSubmit} data-testid="demo-form">
                {demoStatus.message && (
                  <div className={`bp-form-status bp-form-status--${demoStatus.type}`} data-testid="demo-status-message">
                    {demoStatus.type === 'success' && <CheckCircle size={16} />}
                    {demoStatus.message}
                  </div>
                )}
                <div className="bp-form-row">
                  <div className="bp-form-field">
                    <label htmlFor="dm-institution">Institution Name *</label>
                    <input id="dm-institution" type="text" required placeholder="Your Institution" value={demoForm.institutionName} onChange={e => { if (!demoFormStarted.current) { demoFormStarted.current = true; trackFormStart('demo'); } setDemoForm({ ...demoForm, institutionName: e.target.value }); }} data-testid="demo-input-institution" />
                  </div>
                  <div className="bp-form-field">
                    <label htmlFor="dm-person">Contact Person *</label>
                    <input id="dm-person" type="text" required placeholder="Your Name" value={demoForm.contactPerson} onChange={e => { if (!demoFormStarted.current) { demoFormStarted.current = true; trackFormStart('demo'); } setDemoForm({ ...demoForm, contactPerson: e.target.value }); }} data-testid="demo-input-person" />
                  </div>
                </div>
                <div className="bp-form-row">
                  <div className="bp-form-field">
                    <label htmlFor="dm-email">Email *</label>
                    <input id="dm-email" type="email" required placeholder="you@institution.edu.in" value={demoForm.email} onChange={e => { if (!demoFormStarted.current) { demoFormStarted.current = true; trackFormStart('demo'); } setDemoForm({ ...demoForm, email: e.target.value }); }} data-testid="demo-input-email" />
                  </div>
                  <div className="bp-form-field">
                    <label htmlFor="dm-phone">Phone *</label>
                    <div className="bp-phone-field">
                      <CountryCodePicker value={demoCountryCode} onChange={setDemoCountryCode} />
                      <input id="dm-phone" type="tel" required placeholder="XXXXX XXXXX" maxLength={10} pattern="[0-9]{10}" title="Enter a valid 10-digit phone number" value={demoForm.phone} onChange={e => { if (!demoFormStarted.current) { demoFormStarted.current = true; trackFormStart('demo'); } setDemoForm({ ...demoForm, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }); }} data-testid="demo-input-phone" />
                    </div>
                  </div>
                </div>
                <div className="bp-form-row">
                  <div className="bp-form-field">
                    <label htmlFor="dm-date">Preferred Date *</label>
                    <input id="dm-date" type="date" required min={new Date().toISOString().split('T')[0]} value={demoForm.preferredDate} onChange={e => { if (!demoFormStarted.current) { demoFormStarted.current = true; trackFormStart('demo'); } setDemoForm({ ...demoForm, preferredDate: e.target.value }); }} data-testid="demo-input-date" />
                  </div>
                </div>
                <div className="bp-form-field">
                  <label htmlFor="dm-message">Message *</label>
                  <textarea id="dm-message" rows={4} required placeholder="Tell us a bit about your institution and what you'd like to see." value={demoForm.message} onChange={e => { if (!demoFormStarted.current) { demoFormStarted.current = true; trackFormStart('demo'); } setDemoForm({ ...demoForm, message: e.target.value }); }} data-testid="demo-input-message" />
                </div>
                <TurnstileWidget onVerify={setDemoTurnstileToken} onExpire={() => setDemoTurnstileToken(null)} />
                <button type="submit" className="bp-btn-primary bp-btn-form" disabled={demoStatus.type === 'loading' || !demoTurnstileToken} data-testid="demo-submit-button">
                  {demoStatus.type === 'loading' ? 'Submitting…' : 'Request a Demo'}
                  <Send size={15} />
                </button>
              </form>
            </div>
          </div>
        </section>

        {/* ── CONTACT ── */}
        <section id="contact" className="bp-section" data-testid="contact-section">
          <div className="bp-section-inner">
            <div className="bp-section-lede">
              <div className="bp-eyebrow">Contact</div>
              <h2 className="bp-section-headline" data-testid="contact-heading">Get in touch</h2>
              <p className="bp-section-sub" data-testid="contact-description">
                Have a question about the platform, or want to explore how BulandiPe might work for your institution? We'd like to hear from you.
              </p>
            </div>

            <div className="bp-contact-layout">
              <div className="bp-contact-info">
                <div className="bp-contact-detail">
                  <Mail size={18} />
                  <div>
                    <div className="bp-contact-detail-label">Email</div>
                    <a href="mailto:contact@bulandipe.com" className="bp-contact-detail-value">contact@bulandipe.com</a>
                  </div>
                </div>
                <div className="bp-contact-detail">
                  <div className="bp-contact-note">
                    We respond to all institution inquiries within one business day.
                  </div>
                </div>
              </div>

              <form className="bp-form" onSubmit={handleContactSubmit} data-testid="contact-form">
                {submitStatus.message && (
                  <div className={`bp-form-status bp-form-status--${submitStatus.type}`} data-testid="contact-status-message">
                    {submitStatus.type === 'success' && <CheckCircle size={16} />}
                    {submitStatus.message}
                  </div>
                )}
                <div className="bp-form-row">
                  <div className="bp-form-field">
                    <label htmlFor="ct-institution">Institution Name *</label>
                    <input id="ct-institution" type="text" required placeholder="Your Institution" value={contactForm.institutionName} onChange={e => { if (!contactFormStarted.current) { contactFormStarted.current = true; trackFormStart('contact'); } setContactForm({ ...contactForm, institutionName: e.target.value }); }} data-testid="contact-input-institution" />
                  </div>
                  <div className="bp-form-field">
                    <label htmlFor="ct-person">Contact Person *</label>
                    <input id="ct-person" type="text" required placeholder="Your Name" value={contactForm.contactPerson} onChange={e => { if (!contactFormStarted.current) { contactFormStarted.current = true; trackFormStart('contact'); } setContactForm({ ...contactForm, contactPerson: e.target.value }); }} data-testid="contact-input-person" />
                  </div>
                </div>
                <div className="bp-form-row">
                  <div className="bp-form-field">
                    <label htmlFor="ct-email">Email *</label>
                    <input id="ct-email" type="email" required placeholder="you@institution.edu.in" value={contactForm.email} onChange={e => { if (!contactFormStarted.current) { contactFormStarted.current = true; trackFormStart('contact'); } setContactForm({ ...contactForm, email: e.target.value }); }} data-testid="contact-input-email" />
                  </div>
                  <div className="bp-form-field">
                    <label htmlFor="ct-phone">Phone</label>
                    <div className="bp-phone-field">
                      <CountryCodePicker value={contactCountryCode} onChange={setContactCountryCode} />
                      <input id="ct-phone" type="tel" placeholder="XXXXX XXXXX" maxLength={10} pattern="[0-9]{10}" title="Enter a valid 10-digit phone number" value={contactForm.phone} onChange={e => { if (!contactFormStarted.current) { contactFormStarted.current = true; trackFormStart('contact'); } setContactForm({ ...contactForm, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }); }} data-testid="contact-input-phone" />
                    </div>
                  </div>
                </div>
                <div className="bp-form-field">
                  <label htmlFor="ct-message">Message *</label>
                  <textarea id="ct-message" rows={5} required placeholder="How can we help?" value={contactForm.message} onChange={e => { if (!contactFormStarted.current) { contactFormStarted.current = true; trackFormStart('contact'); } setContactForm({ ...contactForm, message: e.target.value }); }} data-testid="contact-input-message" />
                </div>
                <TurnstileWidget onVerify={setContactTurnstileToken} onExpire={() => setContactTurnstileToken(null)} />
                <button type="submit" className="bp-btn-primary bp-btn-form" disabled={submitStatus.type === 'loading' || !contactTurnstileToken} data-testid="contact-submit-button">
                  {submitStatus.type === 'loading' ? 'Sending…' : 'Send Message'}
                  <Send size={15} />
                </button>
              </form>
            </div>
          </div>
        </section>

        {/* ── FOOTER ── */}
        <footer className="bp-footer" data-testid="footer">
          <div className="bp-footer-inner">
            <div className="bp-footer-top">
              <div className="bp-footer-brand">
                <img src="/bulandipe-txt-only-inverted.png" alt="BulandiPe" className="bp-footer-logo" />
                <p className="bp-footer-tagline">Student funding infrastructure for institutions.</p>
              </div>

              <div className="bp-footer-cols">
                <div className="bp-footer-col">
                  <div className="bp-footer-col-title">Platform</div>
                  <button onClick={() => scrollTo('product')} className="bp-footer-link">The Platform</button>
                  <button onClick={() => scrollTo('how-it-works')} className="bp-footer-link">How It Works</button>
                  <button onClick={() => scrollTo('institutions')} className="bp-footer-link">For Institutions</button>
                  <Link to="/help" className="bp-footer-link">Help Center</Link>
                </div>
                <div className="bp-footer-col">
                  <div className="bp-footer-col-title">Legal</div>
                  <button className="bp-footer-link" data-testid="footer-link-privacy" onClick={() => setLegalModal({ open: true, doc: 'privacy', section: null })}>Privacy Policy</button>
                  <button className="bp-footer-link" data-testid="footer-link-terms" onClick={() => setLegalModal({ open: true, doc: 'terms', section: null })}>Terms of Use</button>
                  <button className="bp-footer-link" data-testid="footer-link-cookies" onClick={() => setLegalModal({ open: true, doc: 'privacy', section: 'cookies' })}>Cookie Policy</button>
                </div>
                <div className="bp-footer-col">
                  <div className="bp-footer-col-title">Connect</div>
                  <a href="mailto:contact@bulandipe.com" className="bp-footer-link" onClick={() => trackOutboundClick('email_support')}>contact@bulandipe.com</a>
                  <div className="bp-footer-social" data-testid="social-links">
                    <a href="https://linkedin.com/company/bulandipe" target="_blank" rel="noopener noreferrer" className="bp-social-icon" aria-label="BulandiPe on LinkedIn" data-testid="social-linkedin" onClick={() => trackOutboundClick('linkedin')}><Linkedin size={16} aria-hidden="true" /></a>
                    <a href="https://x.com/bulandipe" target="_blank" rel="noopener noreferrer" className="bp-social-icon" aria-label="BulandiPe on X (Twitter)" data-testid="social-twitter" onClick={() => trackOutboundClick('x_twitter')}><Twitter size={16} aria-hidden="true" /></a>
                    <button className="bp-social-icon" aria-label="BulandiPe on Instagram" data-testid="social-instagram"><Instagram size={16} aria-hidden="true" /></button>
                    <button className="bp-social-icon" aria-label="BulandiPe on YouTube" data-testid="social-youtube"><Youtube size={16} aria-hidden="true" /></button>
                  </div>
                </div>
              </div>
            </div>

            <div className="bp-footer-bottom">
              <span>© {new Date().getFullYear()} BulandiPe, a product of <a href="https://www.instagence.ai/" target="_blank" rel="noopener noreferrer" className="bp-footer-inline-link" onClick={() => trackOutboundClick('instagence')}>Instagence</a>. All rights reserved.</span>
              <span className="bp-footer-disclaimer">All product visuals on this site are representative previews. Names and figures shown are illustrative only.</span>
            </div>
          </div>
        </footer>

      </div>

      {/* ── Cookie Consent Banner — GA4 stays off until Accept is clicked ── */}
      {cookieConsent === null && (
        <CookieConsentBanner
          onAccept={handleCookieAccept}
          onDecline={handleCookieDecline}
          onViewPolicy={() => setLegalModal({ open: true, doc: 'privacy', section: 'cookies' })}
        />
      )}

      {/* ── Legal Document Modal (Privacy Policy / Terms of Service) ── */}
      {legalModal.open && (
        <LegalDocumentModal
          doc={legalModal.doc}
          initialSection={legalModal.section}
          onClose={() => setLegalModal({ open: false, doc: 'privacy', section: null })}
        />
      )}

      {/* ── Plan Interest Modal ── */}
      {planModal.open && (
        <PlanInterestModal
          plan={planModal.plan}
          onClose={() => setPlanModal({ open: false, plan: 'Trial' })}
          onSubmit={handlePlanInterestSubmit}
        />
      )}

      {/* ── Success Modal (shared: contact / partnership / demo) ── */}
      {successModal.open && (() => {
        const v = successModal.variant;
        const name = successModal.institutionName;
        const eyebrow =
          v === 'demo'        ? 'Demo Request Confirmed' :
          v === 'partnership' ? 'Partnership Application Received' :
                                'Message Received';
        const title =
          v === 'demo'        ? `We'll be in touch shortly${name ? `,\n${name}` : ''}.` :
          v === 'partnership' ? `Your application is with us${name ? `,\n${name}` : ''}.` :
                                `Thanks for reaching out${name ? `,\n${name}` : ''}.`;
        const body =
          v === 'demo'        ? "Your demo request has been logged. Someone from our team will reach out within one business day to confirm your session." :
          v === 'partnership' ? "We've received your partnership application and will review it carefully. Expect to hear from us within one business day." :
                                "We've received your message and will get back to you within one business day.";
        return (
          <div className="bp-modal-backdrop" onClick={closeSuccessModal} role="dialog" aria-modal="true" aria-labelledby="success-modal-title">
            <div className="bp-modal-card" onClick={e => e.stopPropagation()}>

              <button className="bp-modal-close" onClick={closeSuccessModal} aria-label="Close">
                <X size={18} />
              </button>

              <div className="bp-modal-icon">
                <svg className="bp-modal-check" viewBox="0 0 52 52" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle className="bp-modal-check-circle" cx="26" cy="26" r="24" stroke="#2563EB" strokeWidth="2.5" />
                  <polyline className="bp-modal-check-tick" points="14,27 22,35 38,18" stroke="#2563EB" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>

              <div className="bp-modal-eyebrow">{eyebrow}</div>
              <h2 className="bp-modal-title" id="success-modal-title">{title}</h2>
              <p className="bp-modal-body">{body}</p>

              <div className="bp-modal-actions">
                <button className="bp-modal-btn-primary" onClick={() => { closeSuccessModal(); scrollTo('product'); }}>
                  Explore the Platform
                </button>
                <button className="bp-modal-btn-ghost" onClick={() => { closeSuccessModal(); scrollTo('home'); }}>
                  Back to Home
                </button>
              </div>

              <div className="bp-modal-progress-track">
                <div className="bp-modal-progress-bar" style={{ width: `${successModalProgress}%` }} />
              </div>
              <p className="bp-modal-dismiss-hint">Closes automatically</p>

            </div>
          </div>
        );
      })()}

    </>
  );
  }
}

export default App;
